import { useCallback, useEffect, useState } from "react";
import Navbar from "../Navbar";
import TreatImage from "./TreatImage";
import { getSentTreats } from "../../services/treatApi";
import "../Pro/Pro.css";
import "./Treat.css";

// =====================================================
// MY SENT TREATS — reader side  (Step 6)  — /my-sent-treats
//
// Only the signed-in reader's own orders (the server scopes the query to
// them), in every state: Successful, Pending, Failed. The blogger's one-time
// thank-you appears on the treat it answers.
// =====================================================

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const STATUS = {
  paid: { label: "Successful", cls: "paid" },
  created: { label: "Pending", cls: "pending" },
  failed: { label: "Failed", cls: "failed" },
};

function MySentTreats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await getSentTreats());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your sent treats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="pro-page-wrap">
      <Navbar />
      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🎁 MY SENT TREATS</div>
          <h1 className="pro-hero-title">Treats you have sent</h1>
          {data && <p className="pro-hero-sub">{data.count} treat{data.count === 1 ? "" : "s"}</p>}
        </header>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>Try again</button>
          </div>
        )}

        {!loading && !error && data && data.items.length === 0 && (
          <div className="pro-card pro-empty">
            <p>You haven't sent any treats yet. Look for “Buy Me a Treat” on a blog you enjoy.</p>
          </div>
        )}

        {!loading && !error && data && data.items.length > 0 && (
          <div className="treat-card-list">
            {data.items.map((t) => {
              const s = STATUS[t.paymentStatus] || STATUS.created;
              return (
                <article key={t._id} className="pro-card treat-sent-card">
                  <div className="treat-sent-head">
                    <TreatImage image={t.treat.image} name={t.treat.name} icon={t.treat.icon} size={52} />
                    <div className="treat-sent-main">
                      <strong>{t.treat.name}</strong>
                      <span>Sent To: {t.blogger.name}</span>
                    </div>
                    <span className={`pro-status-pill pro-status-${s.cls}`}>{s.label}</span>
                  </div>

                  <div className="treat-sent-blog">
                    {t.blog.image && <img src={t.blog.image} alt="" className="treat-blog-thumb" />}
                    {!t.blog.image && t.blog.video && (
                      <video src={t.blog.video} className="treat-blog-thumb" muted preload="metadata" />
                    )}
                    <div>
                      <small className="treat-dim">Blog</small>
                      <p className="treat-sent-blog-title">{t.blog.title || t.blog.content || "—"}</p>
                    </div>
                  </div>

                  <dl className="treat-confirm-list">
                    <div><dt>Amount</dt><dd>Rs. {t.priceRupees}</dd></div>
                    <div><dt>Date and Time</dt><dd>{formatDateTime(t.date)}</dd></div>
                    <div><dt>Status</dt><dd>{s.label}</dd></div>
                    <div><dt>Transaction ID</dt><dd className="treat-mono">{t.transactionId}</dd></div>
                    {t.message && <div><dt>Your greeting</dt><dd>“{t.message}”</dd></div>}
                  </dl>

                  {t.thanks && (
                    <div className="treat-thanks-box">
                      <small>💌 Greeting from {t.blogger.name} · {formatDateTime(t.thanks.at)}</small>
                      <p>“{t.thanks.message}”</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MySentTreats;
